// Packages
import React from "react";
import { connect } from "react-redux";
import { useIntl, FormattedMessage } from 'react-intl';
// Icons, Images etc.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleRight, faTag } from "@fortawesome/free-solid-svg-icons";
// Helpers, Utils etc.
import { formatDecimal } from "../../helpers/itemCalculations";
import {
  translatedName,
} from "../../helpers/translations";
import { populateModifiers } from "../../helpers/utils";

interface IOrderDetailsModalProps {
  pastOrderItem: any;
  handleReorder: any;
  onClose: any;
  currency: any;
}

const OrderDetailsModal = (props: IOrderDetailsModalProps) => {
  let orderDetails = props.pastOrderItem.order_details;
  let subtotal = orderDetails.sub_total;
  let surcharges = orderDetails.surcharges_breakdown || [];
  let discounts = orderDetails.discounts || [];
  let redeemedLoyalty = orderDetails.redeemed_loyalty;
  let tax = orderDetails.tax_total;
  let taxInclusive = orderDetails.is_tax_inclusive;
  let taxBreakdown = orderDetails.tax_group_breakdown || [];
  let tips = orderDetails.tips || 0;
  let totalAmount = orderDetails.total_amount;

  const intl = useIntl();

  const getDateTime = (dateString: any) => {
    let date = new Date(dateString).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    let time = new Date(dateString).toLocaleTimeString("default", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLocaleUpperCase();
    let dateTime = date + " - " + time;
    return dateTime;
  };

  return (
    <div className="mobile-order-details">
      <div className="header">
        <p className="order-number">
          <FormattedMessage
            id="order.order_number_with_hash"
            defaultMessage="Order# {orderNumber}"
            values={{
              orderNumber: props.pastOrderItem.order_number,
            }}
          />
        </p>
        <p className="date-time">
          {getDateTime(props.pastOrderItem.created_at)}
        </p>
      </div>
      <div className="content">
        <div className="order-details-table">
          <div className="table-header">
            <p className="header-name header-qty">
              <FormattedMessage
                id="global.short.quantity"
                defaultMessage="Qty"
              />
            </p>
            <p className="header-name header-item">
              <FormattedMessage
                id="global.item"
                defaultMessage="Item"
              />
            </p>
            <p className="header-name header-amt">
              <FormattedMessage
                id="global.amount"
                defaultMessage="Amount"
              />
            </p>
          </div>
          <div className="order-details">
            {orderDetails.cartItems.map(
              (item: any, index: any) => {
                const selectedModifiers = populateModifiers(item);
                const promotionDetails = orderDetails?.items?.[index]?.discount;
                const promotionName = item.applied_promotion_id && promotionDetails?.name;
                const showDetails = selectedModifiers.length > 0 || promotionName;
                return (
                  <div className="pastorder-item" key={index}>
                    <div className="order">
                      <p className="item-qty">{item.count}x</p>
                      <p className="item-name">
                        {translatedName(item, intl.locale)}
                      </p>
                      <p className="item-amount">
                        {props.currency} {formatDecimal(item.sub_total)}
                      </p>
                    </div>
                    {selectedModifiers.length > 0 ? (
                      <div className="modifiers">
                        {selectedModifiers.map((modifier: any) => {
                          return (
                            <p className="modifier-item" key={modifier.id}>
                              {translatedName(modifier, intl.locale)}
                            </p>
                          );
                        })}
                      </div>
                    ) : null}
                    {promotionName ? (
                      <p className="promotion-name">
                        <FontAwesomeIcon icon={faTag} className="mr-1" />
                        {promotionName}
                      </p>
                    ) : null}
                  </div>
                );
              }
            )}
          </div>
        </div>
        <div className="footer">
          <div className="order-summary">
            <div className="summary-text">
              <p className="subtotal-text">
                <FormattedMessage
                  id="global.subtotal"
                  defaultMessage="Subtotal"
                />
              </p>
              <p className="subtotal-value">{formatDecimal(subtotal)}</p>
            </div>
            {tips ? (
              <div className="summary-text">
                <p>
                  <FormattedMessage
                    id="global.tip"
                    defaultMessage="Tip"
                  />
                </p>
                <p>{formatDecimal(tips)}</p>
              </div>
            ) : null}
            {surcharges.map((surcharge: any, index: any) => {
              return (
                <div className="summary-text" key={index}>
                  <p>{surcharge.name}</p>
                  <p>{formatDecimal(surcharge.amount)}</p>
                </div>
              );
            })}
            {discounts.map((discount: any, index: any) => {
              return (
                <div className="summary-text" key={index}>
                  <p>{discount.name}</p>
                  <p>-{formatDecimal(discount.amount)}</p>
                </div>
              );
            })}
            {redeemedLoyalty > 0 ? (
              <div className="summary-text">
                <p>
                  <FormattedMessage
                    id="global.loyalty"
                    defaultMessage="Loyalty"
                  />
                </p>
                <p>-{formatDecimal(redeemedLoyalty)}</p>
              </div>
            ) : null}
            {!taxInclusive && tax > 0 ? (
              <>
                {taxBreakdown && taxBreakdown.length > 0 ? (
                  taxBreakdown.map((taxItem: any) => {
                    return (
                      <div className="summary-text" key={taxItem.tax_rate_id}>
                        <p>{`${taxItem.tax_name} (${taxItem.tax_rate}%)`}</p>
                        <p>{formatDecimal(taxItem.tax_amount)}</p>
                      </div>
                    );
                  })
                ) : (
                  <div className="summary-text">
                    <p>
                      <FormattedMessage
                        id="global.tax"
                        defaultMessage="Tax"
                      />
                    </p>
                    <p>{formatDecimal(tax)}</p>
                  </div>
                )}
              </>
            ) : null}
            <div className="summary-text total">
              <p>
                <FormattedMessage
                  id="global.total"
                  defaultMessage="Total"
                />
              </p>
              <p>
                {formatDecimal(totalAmount)}
              </p>
            </div>
          </div>
          <div className="option">
            <button
              className="reorder button btn btn-primary"
              onClick={() => props.handleReorder(props.pastOrderItem)}
            >
              <FormattedMessage
                id="my_account.re_order"
                defaultMessage="Re-Order"
              /> <FontAwesomeIcon icon={faChevronCircleRight} size="sm" />
            </button>
            <button
              className="cancel button btn btn-primary"
              onClick={() => props.onClose()}
            >
              <FormattedMessage
                id="global.cancel"
                defaultMessage="Cancel"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  let currency = state.company.currency;

  return {
    currency,
  };
};

export default connect(mapStateToProps, null)(OrderDetailsModal);
